import React, { useState, useRef, useEffect } from 'react';
import { Container, Accordion, AccordionSummary, AccordionDetails, Typography, Box, useTheme, Paper, Slide, IconButton } from '@mui/material';
import { Palette, ExpandMore, Image as ImageIcon, TextFields, Videocam, Visibility, Close } from '@mui/icons-material';
import { PortalScreenHeader } from '../../../../components/PortalScreenHeader';
import { HeaderImagePicker } from './components/HeaderImagePicker';
import { HeaderTextOverlay } from './components/HeaderTextOverlay';
import { PermissionWrapper } from '../../../../components/PermissionWrapper';
import { useStoreConfig } from '../../../../contexts/StoreConfigContext';

export const AppHeader: React.FC = () => {
    const theme = useTheme();
    const { config } = useStoreConfig();

    // Multiple accordions can be expanded at once
    const [expandedPanels, setExpandedPanels] = useState<string[]>(['media']);

    const videoRef = useRef<HTMLVideoElement>(null);
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.muted = true;
        }
    }, []);

    // Floating preview state
    const previewRef = useRef<HTMLDivElement>(null);
    const [showFloatingPreview, setShowFloatingPreview] = useState(false);
    const [floatingPreviewMinimized, setFloatingPreviewMinimized] = useState(false);

    // Scroll-based detection for floating preview
    useEffect(() => {
        const checkPreviewVisibility = () => {
            if (!previewRef.current) return;
            
            const rect = previewRef.current.getBoundingClientRect();
            const previewHeight = rect.height;
            
            // Show floating preview when 1/3 of the preview has scrolled past the top
            const threshold = previewHeight * 0.33;
            const shouldShow = rect.top < -threshold || rect.bottom < 50;
            
            // Auto-show again when scrolling back up to main preview
            if (!shouldShow && floatingPreviewMinimized) {
                setFloatingPreviewMinimized(false);
            }

            setShowFloatingPreview(shouldShow);
        };

        // Check on mount
        checkPreviewVisibility();

        // Listen to scroll events on document and any scrollable parent
        const scrollHandler = () => checkPreviewVisibility();

        window.addEventListener('scroll', scrollHandler, true);
        document.addEventListener('scroll', scrollHandler, true);

        return () => {
            window.removeEventListener('scroll', scrollHandler, true);
            document.removeEventListener('scroll', scrollHandler, true);
        };
    }, [floatingPreviewMinimized]);

    // Toggle accordion expansion (allows multiple open)
    const handlePanelChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanels(prev =>
            isExpanded
                ? [...prev, panel]
                : prev.filter(p => p !== panel)
        );
    };

    const accordionSx = {
        backgroundColor: theme.palette.background.paper,
        borderRadius: '8px !important',
        '&:before': { display: 'none' },
        mb: 2,
        overflow: 'hidden',
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
    };

    const summarysx = {
        minHeight: { xs: 56, sm: 64 },
        '& .MuiAccordionSummary-content': {
            my: { xs: 1.5, sm: 2 },
        },
    };

    // Get font family for overlay text
    const getOverlayFontFamily = () => {
        return config.headerOverlay?.font ? `'${config.headerOverlay.font}', sans-serif` : 'logoFont';
    };

    // Get text shadow CSS
    const getTextShadow = () => {
        if (!config.headerOverlay?.textShadow?.enabled) return 'none';
        const ts = config.headerOverlay.textShadow;
        return `${ts.offsetX}px ${ts.offsetY}px ${ts.blur}px ${ts.color}`;
    };

    // Get vignette styles
    const getVignetteSx = () => {
        if (!config.headerOverlay?.vignette?.enabled) return {};

        const intensity = config.headerOverlay.vignette.intensity;
        const color = config.headerOverlay.vignette.color;
        const alpha = Math.round(intensity * 255).toString(16).padStart(2, '0');

        return {
            '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(ellipse at center, transparent 20%, ${color}${alpha} 100%)`,
                pointerEvents: 'none',
                zIndex: 1,
            }
        };
    };

    // Unified Preview Component (used for both main and floating)
    const HeaderPreview = ({ isFloating = false }: { isFloating?: boolean }) => {
        const overlay = config.headerOverlay;
        const aspectRatio = isFloating ? '1 / 0.30' : { xs: '1 / 0.38', sm: '1 / 0.30', md: '1 / 0.22' };
        const fontSizeMultiplier = isFloating ? 0.35 : 1;

        return (
            <Box
                sx={{
                    width: '100%',
                    aspectRatio,
                    borderRadius: isFloating ? 0 : 4,
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: isFloating ? 'none' : '0px 8px 10px rgba(0, 0, 0, 0.1)',
                    bgcolor: theme.palette.mode === 'dark'
                        ? theme.palette.background.default
                        : theme.palette.grey[200],
                    ...(!config.appHeaderImage || config.appHeaderMediaType === 'image' ? {
                        backgroundImage: config.appHeaderImage
                            ? `url(${config.appHeaderImage})`
                            : `linear-gradient(135deg, ${theme.palette.grey[400]} 0%, ${theme.palette.grey[600]} 100%)`,
                        backgroundSize: 'cover',
                        backgroundPosition: `${config.appHeaderPosition?.x || 50}% ${config.appHeaderPosition?.y || 50}%`,
                        transform: `scale(${config.appHeaderScale || 1})`,
                    } : {}),
                    ...getVignetteSx(),
                }}
            >
                {/* Video background if video type */}
                {config.appHeaderMediaType === 'video' && config.appHeaderImage && (
                    <video
                        ref={videoRef}
                        key={isFloating ? `floating-${config.appHeaderImage}` : config.appHeaderImage}
                        src={config.appHeaderImage}
                        autoPlay
                        muted
                        loop
                        playsInline
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: (config.appHeaderObjectFit || 'cover') as 'cover' | 'contain' | 'fill',
                            objectPosition: `${config.appHeaderPosition?.x || 50}% ${config.appHeaderPosition?.y || 50}%`,
                            transform: `scale(${config.appHeaderScale || 1})`,
                        }}
                    />
                )}
                {/* Overlay Text */}
                {overlay?.enabled && overlay?.text && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: overlay.textAlign === 'left'
                                ? 'flex-start'
                                : overlay.textAlign === 'right'
                                    ? 'flex-end'
                                    : 'center',
                            px: isFloating ? 2 : { xs: 2, md: 6 },
                            transform: `translate(${(overlay.position.x - 50) * 0.5}%, ${(overlay.position.y - 50) * 0.5}%)`,
                            zIndex: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: getOverlayFontFamily(),
                                fontSize: isFloating
                                    ? `${Math.max(overlay.fontSize * fontSizeMultiplier, 0.75)}rem`
                                    : {
                                        xs: `${overlay.fontSize * 0.4}rem`,
                                        sm: `${overlay.fontSize * 0.6}rem`,
                                        md: `${overlay.fontSize}rem`
                                    },
                                fontWeight: overlay.fontWeight,
                                color: overlay.color,
                                letterSpacing: isFloating
                                    ? `${overlay.letterSpacing * 0.5}px`
                                    : `${overlay.letterSpacing}px`,
                                textShadow: getTextShadow(),
                                textAlign: overlay.textAlign,
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.2,
                            }}
                        >
                            {overlay.text}
                        </Typography>
                    </Box>
                )}
                {/* Placeholder text when no content */}
                {!config.appHeaderImage && !overlay?.text && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: isFloating ? 12 : { xs: 14, sm: 18 },
                                fontFamily: 'primaryFont',
                            }}
                        >
                            Upload media or add text overlay
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <PermissionWrapper screen='app-header' permission='view'>
            <Container
                component='main'
                maxWidth='xl'
                sx={{
                    height: '100%',
                    paddingX: { xs: 1, md: 2 },
                    paddingBottom: 4,
                }}
            >
                <PortalScreenHeader
                    title='App Header'
                    subtitle='Customize your storefront header image and text overlay'
                    icon={<Palette />}
                />
                
                {/* Main Preview Section */}
                <Box ref={previewRef} sx={{ mb: 3 }}>
                    <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ mb: 1, display: 'block', fontFamily: 'primaryFont' }}
                    >
                        Live Preview (Matches actual storefront display)
                    </Typography>
                    <HeaderPreview />
                </Box>

                {/* Header Media Section */}
                <Accordion 
                    expanded={expandedPanels.includes('media')} 
                    onChange={handlePanelChange('media')}
                    sx={accordionSx}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={summarysx}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: theme.palette.primary.main + '15',
                                    color: theme.palette.primary.main,
                                }}
                            >
                                {config.appHeaderMediaType === 'video' ? (
                                    <Videocam fontSize='small' />
                                ) : (
                                    <ImageIcon fontSize='small' />
                                )}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography 
                                    sx={{ 
                                        textAlign: 'left',
                                        fontWeight: 600, 
                                        fontFamily: 'primaryFont',
                                        fontSize: { xs: 14, sm: 16 }
                                    }}
                                >
                                    Header Media
                                </Typography>
                                <Typography 
                                    variant='caption' 
                                    color='text.secondary'
                                    sx={{ 
                                        display: { xs: 'none', sm: 'block' },
                                        fontFamily: 'primaryFont'
                                    }}
                                >
                                    Upload and configure your header image or video
                                </Typography>
                            </Box>
                            {config.appHeaderImage && (
                                <Box
                                    sx={{
                                        ml: 'auto',
                                        mr: 1,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        backgroundColor: theme.palette.success.main + '20',
                                        color: theme.palette.success.main,
                                        fontSize: 10,
                                        fontWeight: 600,
                                        fontFamily: 'primaryFont',
                                        display: { xs: 'none', sm: 'block' }
                                    }}
                                >
                                    {config.appHeaderMediaType === 'video' ? 'VIDEO' : 'IMAGE'} SET
                                </Box>
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                        <HeaderImagePicker />
                    </AccordionDetails>
                </Accordion>

                {/* Text Overlay Section */}
                <Accordion
                    expanded={expandedPanels.includes('overlay')}
                    onChange={handlePanelChange('overlay')}
                    sx={accordionSx}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={summarysx}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: theme.palette.secondary.main + '15',
                                    color: theme.palette.secondary.main,
                                }}
                            >
                                <TextFields fontSize='small' />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography 
                                    sx={{ 
                                        textAlign: 'left',
                                        fontWeight: 600, 
                                        fontFamily: 'primaryFont',
                                        fontSize: { xs: 14, sm: 16 }
                                    }}
                                >
                                    Text Overlay
                                </Typography>
                                <Typography 
                                    variant='caption' 
                                    color='text.secondary'
                                    sx={{ 
                                        display: { xs: 'none', sm: 'block' },
                                        fontFamily: 'primaryFont'
                                    }}
                                >
                                    Add customizable text on top of your header
                                </Typography>
                            </Box>
                            {config.headerOverlay?.enabled && (
                                <Box
                                    sx={{
                                        ml: 'auto',
                                        mr: 1,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        backgroundColor: theme.palette.success.main + '20',
                                        color: theme.palette.success.main,
                                        fontSize: 10,
                                        fontWeight: 600,
                                        fontFamily: 'primaryFont',
                                        display: { xs: 'none', sm: 'block' }
                                    }}
                                >
                                    ENABLED
                                </Box>
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                        <HeaderTextOverlay />
                    </AccordionDetails>
                </Accordion>

                {/* Floating Preview - Shows when main preview scrolls out of view */}
                <Slide direction='up' in={showFloatingPreview && !floatingPreviewMinimized} mountOnEnter unmountOnExit>
                    <Paper
                        elevation={8}
                        sx={{
                            position: 'fixed',
                            bottom: 16,
                            right: 16,
                            zIndex: 1200,
                            borderRadius: 2,
                            overflow: 'hidden',
                            width: { xs: 320, sm: 400, md: 500 },
                            border: `2px solid ${theme.palette.primary.main}`,
                        }}
                    >
                        {/* Header bar */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.5,
                                py: 0.5,
                                bgcolor: theme.palette.primary.main,
                                color: 'white',
                            }}
                        >
                            <Visibility sx={{ fontSize: 16 }} />
                            <Typography variant='caption' sx={{ fontWeight: 600, fontSize: 11 }}>
                                Live Preview
                            </Typography>
                            <IconButton
                                size='small'
                                onClick={() => setFloatingPreviewMinimized(true)}
                                sx={{
                                    ml: 'auto',
                                    color: 'white',
                                    p: 0.25,
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                    },
                                }}
                            >
                                <Close sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>
                        {/* Mini preview */}
                        <HeaderPreview isFloating />
                    </Paper>
                </Slide>

                {/* Minimized FAB - Shows when floating preview is minimized */}
                <Slide direction='up' in={showFloatingPreview && floatingPreviewMinimized} mountOnEnter unmountOnExit>
                    <Paper
                        elevation={6}
                        onClick={() => setFloatingPreviewMinimized(false)}
                        sx={{
                            position: 'fixed',
                            bottom: 16,
                            right: 16,
                            zIndex: 1200,
                            borderRadius: 3,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1,
                            bgcolor: theme.palette.primary.main,
                            color: 'white',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: theme.shadows[12],
                            },
                        }}
                    >
                        <Visibility sx={{ fontSize: 18 }} />
                        <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                            Preview
                        </Typography>
                    </Paper>
                </Slide>
            </Container>
        </PermissionWrapper>
    );
};

export default AppHeader;
